import { Link } from "react-router-dom"

export default function TermsPage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-4xl">
        {/* Back navigation */}
        <Link 
          to="/" 
          className="inline-block mb-8 text-white/70 hover:text-white transition-colors"
        >
          ← Back to Home
        </Link>

        {/* Glassmorphism container */}
        <div className="rounded-3xl bg-white/5 backdrop-blur-lg border border-white/10 p-8 md:p-12 shadow-xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Terms of Service</h1>
          
          <p className="text-white/60 mb-8">
            Last updated: November 10, 2025
          </p>

          <div className="space-y-8 text-white/90">
            {/* Introduction */}
            <section>
              <p className="leading-relaxed">
                Welcome to Voy. These Terms of Service ("Terms") govern your access to and use of our AI-powered travel planning platform. By using Voy, you agree to be bound by these Terms. If you do not agree, please do not use our services.
              </p>
            </section>

            {/* 1. Service Description */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Service Description</h2>
              <p className="leading-relaxed">
                Voy is an AI-powered travel planning platform that helps users search, compare, and book flights, hotels, and create personalized itineraries. We aggregate data from multiple travel providers to offer comprehensive travel planning services.
              </p>
            </section>

            {/* 2. User Accounts */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. User Accounts and Registration</h2>
              
              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">2.1 Account Creation</h3>
              <p className="leading-relaxed mb-4">
                To use certain features, you must create an account. You agree to provide accurate, current information and keep it updated.
              </p>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">2.2 Account Security</h3>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li>You are responsible for maintaining the confidentiality of your password</li>
                <li>You are responsible for all activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>We reserve the right to suspend accounts showing suspicious activity</li>
              </ul>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">2.3 Account Termination</h3>
              <p className="leading-relaxed">
                You may delete your account at any time. We may suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
              </p>
            </section>

            {/* 3. Acceptable Use */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Acceptable Use Policy</h2>
              <p className="leading-relaxed mb-4">You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li>Use the service for illegal purposes</li>
                <li>Impersonate others or provide false information</li>
                <li>Attempt to access unauthorized areas of the platform</li>
                <li>Interfere with or disrupt the service</li>
                <li>Scrape, copy, or resell our content or data</li>
                <li>Use automated systems to abuse the service</li>
                <li>Violate intellectual property rights</li>
              </ul>
            </section>

            {/* 4. Travel Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Travel Services and Third-Party Providers</h2>
              
              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">4.1 Aggregator Service</h3>
              <p className="leading-relaxed mb-4">
                Voy is a travel aggregator and search platform. We do not operate flights, hotels, or other travel services. All bookings are fulfilled by third-party travel providers.
              </p>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">4.2 Disclaimer</h3>
              <ul className="list-disc list-inside space-y-2 text-white/80 ml-4">
                <li>We are not responsible for changes, cancellations, or issues with bookings</li>
                <li>Prices and availability are subject to change without notice</li>
                <li>Travel providers' terms and conditions apply to all bookings</li>
                <li>We strive for accuracy but cannot guarantee all information is current</li>
                <li>AI-generated itineraries are suggestions, not guarantees</li>
              </ul>

              <h3 className="text-xl font-semibold text-white/90 mb-3 mt-4">4.3 Your Responsibility</h3>
              <p className="leading-relaxed">
                You are responsible for verifying all travel details, documentation (passports, visas), travel insurance, and compliance with destination requirements before booking.
              </p>
            </section>

            {/* 5. Payments */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Payments and Refunds</h2>
              <p className="leading-relaxed mb-4">
                Payment processing is handled securely through Stripe. Refund policies are determined by the respective travel providers. Voy does not control or process refunds for third-party bookings.
              </p>
            </section>

            {/* 6. Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property</h2>
              <p className="leading-relaxed">
                All content, features, and functionality on Voy (including AI models, algorithms, design, text, graphics, logos) are owned by Voy and protected by intellectual property laws. You may not copy, modify, distribute, or reverse engineer any part of our platform without written permission.
              </p>
            </section>

            {/* 7. Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Limitation of Liability</h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, VOY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR SERVICE INTERRUPTION. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO VOY IN THE PAST 12 MONTHS.
              </p>
            </section>

            {/* 8. Disclaimer of Warranties */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimer of Warranties</h2>
              <p className="leading-relaxed">
                OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT GUARANTEE UNINTERRUPTED, SECURE, OR ERROR-FREE SERVICE.
              </p>
            </section>

            {/* 9. Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Governing Law and Disputes</h2>
              <p className="leading-relaxed">
                These Terms are governed by the laws of Argentina. Any disputes shall be resolved through binding arbitration or in the courts of Buenos Aires, Argentina.
              </p>
            </section>

            {/* 10. Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or platform notification. Continued use after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            {/* 11. Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Contact Information</h2>
              <p className="leading-relaxed mb-4">
                For questions, concerns, or disputes regarding these Terms of Service, please contact us:
              </p>
              <div className="p-4 rounded-2xl bg-white/10 border border-white/20">
                <p className="text-white/90">
                  <strong>Email:</strong> <a href="mailto:social@voy.earth" className="text-primary hover:underline">social@voy.earth</a><br />
                  <strong>Website:</strong> <a href="https://alpha.voy.earth" className="text-primary hover:underline">alpha.voy.earth</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
} 
